import { Language, Mail, Phone } from '@mui/icons-material';
import { Grid, Typography } from '@renderer-ui-library/mui';
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { BlockProps } from 'blocks/BlockProps';
import classNames from 'classnames';
import { memo, useCallback, useContext, useMemo } from 'react';
import { ISpecialistColumnsBlockData } from './ISpecialistColumnsBlockData';
import styles from './specialistColumnsBlock.module.scss';

import { useTranslatedLanguages } from '@internal/i18n/useTranslatedLanguages';
import {
    CustomerServiceNumber,
    SettingsContext,
} from '@internal/page/SettingsContext';
import { TSalesManager } from '@internal/services/TSalesManager';
import { BlockTopText } from '@renderer-ui-library/molecules';
import { ProfilePicture } from '@renderer-ui-library/molecules/Media/ProfilePicture';
import Link from 'next/link';
import { MachineContext } from 'utils/machine/MachineContext';
import { tracker } from 'utils/tracking/tracker';
import { TServerSideSpecialistColumnsBlockProps } from './TServerSideSpecialistColumnsBlockProps';

type ColumnData = {
    serviceNumber: CustomerServiceNumber | undefined;
    languages: string;
    person: TSalesManager;
};

export const SpecialistColumnsBlock: React.FC<
    BlockProps<
        ISpecialistColumnsBlockData,
        TServerSideSpecialistColumnsBlockProps
    >
> = memo(({ preFetchedBlockProps, data, locale }) => {
    const { machine } = useContext(MachineContext);
    const { customerServiceNumbers } = useContext(SettingsContext);
    const { translateLanguages } = useTranslatedLanguages();

    const handlePhoneClick = useCallback(
        () =>
            tracker.trackEvent({
                name: 'call-click',
                target_name: 'specialist',
                is_interaction: true,
                machine_group_id: machine?.machineGroupId,
            }),
        [machine],
    );

    const columns = useMemo<ColumnData[]>(() => {
        const serviceNumber = customerServiceNumbers[locale];

        return preFetchedBlockProps.people.map<ColumnData>(zohoUser => ({
            serviceNumber,
            languages: translateLanguages(zohoUser.languagesSpoken).join(', '),
            person: zohoUser,
        }));
    }, [
        customerServiceNumbers,
        locale,
        preFetchedBlockProps.people,
        translateLanguages,
    ]);

    if (columns.length === 0) {
        return null;
    }

    return (
        <BlockWrapper blockData={data}>
            {(data.topText || data.title) && (
                <BlockTopText
                    title={data.title}
                    text={data.topText}
                    highlight={data.highlight}
                />
            )}
            <div className={styles.gridSlider}>
                <Splide
                    options={{
                        type: 'slide',
                        heightRatio: 2 / 3,
                        pagination: true,
                        perPage: 3,

                        gap: '2.5rem',
                        pauseOnHover: true,
                        pauseOnFocus: true,
                        height: 'auto',
                        autoplay: false,
                        rewind: true,
                        arrows: false,
                        breakpoints: {
                            768: {
                                perPage: 1,
                                gap: '1rem',
                                padding: {
                                    right: '4rem',
                                },
                            },
                        },
                    }}
                >
                    {columns.map(column => {
                        const { languages, serviceNumber, person } = column!;

                        return (
                            <SplideSlide key={person.id}>
                                <Grid
                                    item
                                    xs={12}
                                    md={4}
                                    className={classNames(styles.gridItem, {
                                        [styles.highlight]: data.highlight,
                                    })}
                                >
                                    <div className={styles.cardContainer}>
                                        <div>
                                            <div
                                                className={
                                                    styles.profilePictureContainer
                                                }
                                            >
                                                <ProfilePicture
                                                    variant="avatar"
                                                    salesManager={person}
                                                    size={140}
                                                />
                                            </div>

                                            <div
                                                className={
                                                    styles.titleContainer
                                                }
                                            >
                                                <Typography
                                                    variant="h6"
                                                    className={styles.title}
                                                >
                                                    {person.bioTitle}
                                                </Typography>
                                            </div>
                                            <Typography variant="body1">
                                                {person.bioText}
                                            </Typography>
                                        </div>

                                        <div
                                            className={
                                                styles.contactInfoContainer
                                            }
                                        >
                                            <div
                                                className={
                                                    styles.contactInfoItem
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.contactInfoKey
                                                    }
                                                >
                                                    <Language
                                                        className={
                                                            styles.contactInfoIcon
                                                        }
                                                    />
                                                    <Typography variant="body2">
                                                        {languages}
                                                    </Typography>
                                                </div>
                                            </div>

                                            {serviceNumber && (
                                                <div
                                                    className={
                                                        styles.contactInfoItem
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.contactInfoKey
                                                        }
                                                        onClick={
                                                            handlePhoneClick
                                                        }
                                                    >
                                                        <Phone
                                                            className={
                                                                styles.contactInfoIcon
                                                            }
                                                        />
                                                        <Typography variant="body2">
                                                            <Link
                                                                href={`tel:${serviceNumber.linkNumber}`}
                                                                className={
                                                                    styles.link
                                                                }
                                                            >
                                                                {
                                                                    serviceNumber.displayNumber
                                                                }
                                                            </Link>
                                                        </Typography>
                                                    </div>
                                                </div>
                                            )}

                                            <div
                                                className={
                                                    styles.contactInfoItem
                                                }
                                            >
                                                <div
                                                    className={
                                                        styles.contactInfoKey
                                                    }
                                                >
                                                    <Mail
                                                        className={
                                                            styles.contactInfoIcon
                                                        }
                                                    />
                                                    <Typography variant="body2">
                                                        <Link
                                                            href={`mailto:${person.email}`}
                                                            className={
                                                                styles.link
                                                            }
                                                        >
                                                            {person.email}
                                                        </Link>
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                            </SplideSlide>
                        );
                    })}
                </Splide>
            </div>
        </BlockWrapper>
    );
});

SpecialistColumnsBlock.displayName = 'SpecialistColumnsBlock';
